
import {Component, Prop, Provide, Vue, Watch} from 'vue-property-decorator';
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import FormatHelper from '@/helpers/format-helper';
import PriceHelper from '@/helpers/price-helper';
import BudgetService from '@/services/BudgetService';
import ApiHelper from '@/helpers/api-helper';
import TabBudgetDestination from '@/components/inputs/TabBudgetDestination.vue';
import TabBudgetAccommodation from '@/components/inputs/TabBudgetAccommodation.vue';
import moment from 'moment';
import TabBudgetTransport from '@/components/inputs/TabBudgetTransport.vue';
import TabBudgetExtraServices from '@/components/inputs/TabBudgetExtraServices.vue';
import TabBudgetActivities from '@/components/inputs/TabBudgetActivities.vue';
import Budget from '@/models/budget';
import {ESPERA_DE_PRECIOS, EsperaDePrecios, nuevaEspera, textoDeEspera} from '@/helpers/espera-de-precios';
import VentanaAlGuardar from '@/components/modals/VentanaAlGuardar.vue';
import {CambiosAlGuardar, juntar} from '@/helpers/ventana-al-guardar';
import {fechasDelViaje} from '@/helpers/budget-list';

// La pestaña «Servicios extras» (0 Destino, 1 Alojamiento, 2 Transporte, 3 Servicios, 4 Actividades).
const PESTANA_SERVICIOS = 3;

// Lo que «Destino y Cliente» le pasa al presupuesto (`setBudget`).
const CAMPOS_DE_DESTINO = [
	'start_date', 'end_date', 'students', 'teachers', 'who', 'center', 'customer_state_id', 'ages_id',
	'responsible_name_surname', 'responsible_email', 'responsible_phone', 'others',
];

@Component({
	components: {
		TabBudgetActivities,
		TabBudgetExtraServices,
		TabBudgetTransport,
		TabBudgetAccommodation,
		TabBudgetDestination,
		VentanaAlGuardar,
		ValidationProvider,
		ValidationObserver,
	},
})
export default class CustomerBudgetForm extends Vue {
	@Prop({ default: null, required: true }) private edit_id;

	$refs!: {
		obs: InstanceType<typeof ValidationObserver>;
		tabBudgetDestination: InstanceType<typeof TabBudgetDestination>;
		tabBudgetAccommodation: InstanceType<typeof TabBudgetAccommodation>;
		tabBudgetTransport: InstanceType<typeof TabBudgetTransport>;
		tabBudgetExtraServices: InstanceType<typeof TabBudgetExtraServices>;
		tabBudgetActivities: InstanceType<typeof TabBudgetActivities>;
	}

	private loading = true;
	private activeTab = 0;
	private total: any;
	private budget: Budget = new Budget();
	private people: any;
	private students: any;
	private days: any;
	// N-235: plazas de profesor de pago que ocupan los extras marcados (el coordinador).
	private teacherSeats = 0;
	// N-287 B3: el servidor rechaza una tarifa de alojamiento que no cubre fechas y personas; su
	// mensaje se enseña en la pestaña Alojamiento, debajo de Precios.
	private erroresAlojamiento: any[] = [];
	// N-287 C2: ídem para los servicios extra (450 bajo `extra_service`), en su pestaña.
	private erroresServicios: any[] = [];
	// N-288 · 4A: los catálogos de precios en camino y el número que se está tecleando; cada
	// pestaña la recoge con `inject`. Y, de paso (8-oct), GUARDAR en gris mientras guarda.
	@Provide(ESPERA_DE_PRECIOS) private esperaDePrecios: EsperaDePrecios = nuevaEspera();
	private guardando = false;
	// N-288 · T3: lo que enseña la ventana de GUARDAR (null, cerrada), y los pasajeros y las fechas al
	// abrir la ficha, para decir qué ha cambiado («Has cambiado los pasajeros de 42 a 57.»).
	private ventana: any = null;
	private alAbrir: any = null;
	// N-291 (T3): la ficha TAL COMO LLEGÓ del servidor (cabecera y precios), y el presupuesto al abrirla
	// en texto, para saber si hay cambios sin guardar (GUARDAR manda exactamente `budget`). Ojo: al
	// leer sus catálogos, las pestañas lo COMPLETAN solas (suplementos y extras del alojamiento, las
	// líneas del transporte; medido en la réplica, 5 fichas, ~0,3 s): la foto buena es la de cuando
	// llega la primera tanda de catálogos (`alLlegarLosCatalogos`), no la del servidor.
	private servidor: any = {};
	private alCargar: string | null = null;
	private fotoTrasLosCatalogos = false;
	private avisoGuardarAntes = false;
	private avisoCerrar = false;

	constructor() {
		super();
		this.people = 0;
		this.students = 0;
		this.days = 0;
		this.total = {
			accommodation_price: 0,
			transport_price: 0,
			extra_service_price: 0,
			activity_price: 0,
		};
	}

	mounted() {
		if (this.edit_id) {
			this.loading = true;
			BudgetService.get(this.edit_id)
				.then(res => {
					if (res.status === 200) {
						this.servidor = res.data;
						this.budget = Budget.copy(res.data);
						this.people = this.budget.students + this.budget.teachers;
						this.students = this.budget.students;
						const days = moment(this.budget.end_date).diff(moment(this.budget.start_date), 'days') + 1;
						this.days = days;
						this.total.accommodation_price = this.budget.accommodation.price * (days -1);
						this.alAbrir = {
							pasajeros: this.people,
							start_date: this.budget.start_date,
							end_date: this.budget.end_date,
						};
						this.alCargar = JSON.stringify(this.budget);
						this.loading = false;
						// Con las pestañas ya montadas, la foto con lo que tiene cada una (si ningún catálogo
						// llegara nunca, esta es la que vale).
						this.$nextTick(() => {
							if (!this.fotoTrasLosCatalogos) {
								this.alCargar = this.loQueSeGuardaria();
							}
						});
					}
				})
				.catch(error => {
					ApiHelper.handleErrors(error, {}, this.$bus);
				});
		}
	}

	/**
	 * N-291 (6A): la primera tanda de catálogos ya ha llegado (la espera de precios vuelve a 0): lo que
	 * las pestañas completaron al leerlos no es un cambio del operador. Solo la primera: las tandas que
	 * pide un cambio de pasajeros o de fechas SÍ son cambios sin guardar.
	 */
	@Watch('esperaDePrecios.enCamino')
	alLlegarLosCatalogos(ahora: number, antes: number) {
		if (this.fotoTrasLosCatalogos || this.alCargar === null || ahora !== 0 || !(antes > 0)) {
			return;
		}
		this.fotoTrasLosCatalogos = true;
		this.$nextTick(() => {
			this.alCargar = this.loQueSeGuardaria();
		});
	}

	/**
	 * N-291 (6A): lo que GUARDAR mandaría ahora, en texto. «Destino y Cliente», «Servicios extras» y
	 * «Actividades» trabajan sobre su copia y no la pasan al presupuesto hasta validar (cambiar de
	 * pestaña o GUARDAR): un cambio ahí aún no está en `budget` (visto en la réplica: el colegio).
	 */
	loQueSeGuardaria(): string {
		const pestanas: any = this.$refs || {};
		const foto: any = Object.assign({}, this.budget);
		const destino = pestanas.tabBudgetDestination ? pestanas.tabBudgetDestination.destination_budget : null;
		if (destino) {
			CAMPOS_DE_DESTINO.forEach((campo) => {
				foto[campo] = destino[campo];
			});
		}
		const servicios = pestanas.tabBudgetExtraServices ? pestanas.tabBudgetExtraServices.extra_service_budget : null;
		if (servicios) {
			foto.extra_service = servicios.extra_service;
		}
		const actividades = pestanas.tabBudgetActivities ? pestanas.tabBudgetActivities.daysData : undefined;
		if (Array.isArray(actividades)) {
			foto.activities = actividades;
		}
		return JSON.stringify(foto);
	}

	/** N-291 (6A): ¿ya no es el que se abrió? Método y no `get`: lee las pestañas por `$refs`, que no avisa. */
	hayCambios(): boolean {
		return this.alCargar !== null && this.loQueSeGuardaria() !== this.alCargar;
	}

	/** N-291 (6A): profesor · colegio · destino y fechas · grupo · Intranet, como se guardó. */
	get datosDeLaCabecera(): { texto: string; destacado?: boolean }[] {
		const ficha = this.servidor || {};
		const datos: { texto: string; destacado?: boolean }[] = [];
		if (ficha.responsible_name_surname) {
			datos.push({ texto: ficha.responsible_name_surname });
		}
		if (ficha.center) {
			datos.push({ texto: ficha.center });
		}
		const fechas = fechasDelViaje(ficha.start_date || null, ficha.end_date || null);
		const destino = [ficha.travel_name, fechas].filter(Boolean).join(', ');
		if (destino) {
			datos.push({ texto: destino, destacado: true });
		}
		datos.push({ texto: ficha.students + ' + ' + ficha.teachers });
		datos.push({
			texto: this.$t(ficha.external_sync ? 'budget_header_intranet_synced' : 'budget_header_intranet_not_synced') as string,
		});
		return datos;
	}

	/**
	 * N-291 (3A): el precio por alumno del PDF (`price_per_student`, el mismo del listado). Sin
	 * alumnos no hay, como en el PDF. Si el servidor aún no lo manda, el de siempre (P-13).
	 */
	get precioPorAlumno(): string {
		const precio = this.servidor ? this.servidor.price_per_student : undefined;
		if (precio === undefined) {
			return this.precioReal + ' €';
		}
		return precio === null ? '—' : FormatHelper.formatPrice(precio) + ' €';
	}

	/** N-292: el total GUARDADO, como el precio por alumno. Sin alumnos no hay; sin el dato del servidor, no sale. */
	get totalDelViaje(): string | undefined {
		const total = this.servidor ? this.servidor.group_total : undefined;
		if (total === undefined) {
			return undefined;
		}
		return total === null ? '—' : FormatHelper.formatPrice(total) + ' €';
	}

	/** N-291 (6A): los botones de la cabecera; con cambios sin guardar, piden guardar antes. */
	pedir(accion: string) {
		if (this.hayCambios()) {
			this.avisoGuardarAntes = true;
			return;
		}
		this.$emit(accion, this.budget.id);
	}

	/** N-291 (6A): la flecha; con cambios sin guardar, pregunta antes. */
	alCerrar() {
		if (this.hayCambios()) {
			this.avisoCerrar = true;
			return;
		}
		this.$emit('close');
	}

	cerrarSinGuardar() {
		this.avisoCerrar = false;
		this.$emit('close');
	}

	/**
	 * N-288 · T3 (1B · 2A · 3A, decisiones de Bernardo del 7-oct-2026) · GUARDAR valida y, si al guardar
	 * se quita algo o algo cambia de precio, lo enseña en una ventana: «Volver y revisar» / «Guardar así».
	 * Si no cambia nada, guarda sin preguntar. La ventana sustituye al salto provisional a «Servicios
	 * extras» de N-287 C (8-oct).
	 */
	async alPulsarGuardar() {
		// De paso (N-288, 8-oct): un doble clic mandaba dos guardados. Con la ventana abierta, tampoco.
		if (this.guardando) {
			return;
		}
		this.guardando = true;
		if (!(await this.validarParaGuardar())) {
			this.guardando = false;
			return;
		}
		const cambios = this.cambiosAlGuardar();
		if (!cambios.quitan.length && !cambios.cambian.length) {
			this.mandar([]);
			return;
		}
		this.ventana = Object.assign(this.textosDeLaVentana(cambios), { huecos: cambios.huecos });
	}

	volverYRevisar() {
		this.ventana = null;
		this.guardando = false;
	}

	/** 3A: lo que no tiene precio se quita al guardar (las actividades, del envío). */
	guardarAsi() {
		const huecos = this.ventana ? this.ventana.huecos : [];
		this.ventana = null;
		this.mandar(huecos);
	}

	/** Valida y guarda tal cual, sin ventana: lo que hacía GUARDAR antes de N-288 T3. */
	async update() {
		if (this.guardando) {
			return;
		}
		this.guardando = true;
		const valid = await this.validarParaGuardar();
		// Si no pasa la validación, GUARDAR vuelve a estar activo.
		this.guardando = valid;
		if (valid) {
			this.mandar([]);
		}
	}

	validarParaGuardar() {
		this.erroresAlojamiento = [];
		this.erroresServicios = [];
		return this.validateAllTab();
	}

	/** `huecos`: las medias jornadas que no se mandan ([{day, parte}]); el servidor borra lo que no llega. */
	mandar(huecos) {
		const component = this;
		BudgetService.update(huecos.length ? this.sinLosHuecos(huecos) : this.budget)
			.then(res => {
				this.guardando = false;
				if (res.status === 200) {
					// N-288 · T3 (8-oct): la ficha se cerraba sin decir nada.
					this.$bus.$emit('success', { message: 'Presupuesto guardado' });
					this.$emit('saved');
				}
			})
			.catch(error => {
				this.guardando = false;
				const rechazo = error.response && error.response.status == 450 ? error.response.data : null;
				if (rechazo && rechazo.accommodation) {
					this.erroresAlojamiento = rechazo.accommodation;
					this.activeTab = 1;
				}
				if (rechazo && rechazo.extra_service) {
					this.erroresServicios = rechazo.extra_service;
					if (!rechazo.accommodation) {
						this.activeTab = PESTANA_SERVICIOS;
					}
				}
				ApiHelper.handleErrors(error, component.$refs['obs'], this.$bus);
			});
	}

	/** Una copia del presupuesto sin esas medias jornadas: la parrilla del operador no se toca. */
	sinLosHuecos(huecos) {
		const activities = (this.budget.activities || []).map((dia, indice) => {
			const copia = Object.assign({}, dia);
			for (const hueco of huecos) {
				if (hueco.day === indice && copia[hueco.parte]) {
					copia[hueco.parte] = Object.assign({}, copia[hueco.parte], { activity_id: null });
				}
			}
			return copia;
		});
		return Object.assign({}, this.budget, { activities });
	}

	/** Lo que cada pestaña dice que se quita o cambia de precio al guardar, en el orden de las pestañas. */
	cambiosAlGuardar(): CambiosAlGuardar & { huecos: any[] } {
		const actividades = this.$refs.tabBudgetActivities.cambiosAlGuardar();
		const cambios = juntar([
			this.$refs.tabBudgetAccommodation.cambiosAlGuardar(),
			this.$refs.tabBudgetTransport.cambiosAlGuardar(),
			this.$refs.tabBudgetExtraServices.cambiosAlGuardar(),
			actividades,
		]);
		return Object.assign(cambios, { huecos: actividades.huecos });
	}

	/** La entradilla y los títulos de la ventana (maqueta `1b-ventana-al-guardar.jpg`). */
	textosDeLaVentana(cambios: CambiosAlGuardar) {
		const antes = this.alAbrir || {};
		const pasajeros = Number(this.people);
		const otrosPasajeros = antes.pasajeros !== undefined && Number(antes.pasajeros) !== pasajeros;
		const otrasFechas = antes.start_date !== undefined
			&& (antes.start_date !== this.budget.start_date || antes.end_date !== this.budget.end_date);
		const rango = (inicio, fin) => 'del ' + FormatHelper.formatDate(String(inicio).substring(0, 10))
			+ ' al ' + FormatHelper.formatDate(String(fin).substring(0, 10));
		const entradilla: string[] = [];
		if (otrosPasajeros) {
			entradilla.push('Has cambiado los pasajeros de ' + antes.pasajeros + ' a ' + pasajeros + '.');
		}
		if (otrasFechas) {
			entradilla.push('Has cambiado las fechas: ahora ' + rango(this.budget.start_date, this.budget.end_date)
				+ ' (antes, ' + rango(antes.start_date, antes.end_date) + ').');
		}
		const porPersonas = otrosPasajeros && cambios.quitan.every((quitado) => quitado.causa === 'personas');
		return {
			entradilla: entradilla.join(' '),
			tituloQuitan: porPersonas ? 'Se quitan: no tienen precio para ' + pasajeros + ' pasajeros' : 'Se quitan',
			quitan: cambios.quitan,
			tituloCambian: otrosPasajeros && !otrasFechas ? 'Cambian de precio: pasan al tramo de ' + pasajeros : 'Cambian de precio',
			cambian: cambios.cambian,
		};
	}

	async validateAllTab() {
		let valid = false;
		for (let i = 0; i <= 4; i++) {
			this.activeTab = i;
			valid = await this.validateTab(i);
			if (!valid) {
				break;
			}
		}
		return valid;
	}

	get isDisabledTab() {
		return false;
	}

	async validateTab(tab) {
		let valid = false;
		if (tab == 0) {
			valid = await this.$refs['tabBudgetDestination'].validate();
		}
		if (tab == 1) {
			valid = await this.$refs['tabBudgetAccommodation'].validate();
		}
		if (tab == 2) {
			valid = await this.$refs['tabBudgetTransport'].validate();
		}
		if (tab == 3) {
			valid = await this.$refs['tabBudgetExtraServices'].validate();
		}
		if (tab == 4) {
			valid = await this.$refs['tabBudgetActivities'].validate();
		}
		if (!valid) {
			this.activeTab = tab;
		}
		return valid;
	}

	setBudget(data) {
		CAMPOS_DE_DESTINO.forEach((campo) => {
			this.budget[campo] = data[campo];
		});
		this.people = parseInt(this.budget.students) + parseInt(this.budget.teachers);
		const days = moment(this.budget.end_date).diff(moment(this.budget.start_date), 'days') + 1;
		this.days = days;
	}

	// N-234: las unidades contratadas de cada servicio extra, tal como las devuelve el
	// selector. Se guardan en el presupuesto —no dentro de `extra_service`, que es la
	// identidad de las casillas— y `BudgetService.update` las reparte al enviar.
	setQuantity(quantities) {
		this.budget.extra_service_quantity = quantities;
	}

	// N-235: las plazas que cuenta el selector de extras. Suben aparte del precio: el
	// importe de los extras son sus honorarios, y la plaza se cobra en el recargo.
	setTeacherSeats(seats) {
		this.teacherSeats = Number(seats) || 0;
	}

	setPrice(value, type) {
		if (type == 'accommodation_price') {
			this.total[type] = value * (this.days - 1);
		}  else{
			this.total[type] = value;
		}
	}

	// N-288 · 4A: «Actualizando los precios para N pasajeros…», o '' si no hay que esperar.
	get textoDeEspera() {
		return textoDeEspera(this.esperaDePrecios, this.people);
	}

	// P-13: en un presupuesto ya guardado se muestra el PRECIO REAL del backend
	// (`total_amount`, el mismo del listado y el PDF), no el recálculo en vivo `sumTotal`
	// (que usaba otra fórmula/redondeo y descuadraba). En un presupuesto nuevo sin guardar
	// todavía no hay `total_amount`, así que se cae al cálculo en vivo como estimación.
	get precioReal() {
		if (this.budget && this.budget.total_amount != null) {
			return FormatHelper.formatPrice(this.budget.total_amount);
		}
		return this.sumTotal;
	}

	get sumTotal() {
		let sum = 0;
		for (const el in this.total) {
			if (Object.prototype.hasOwnProperty.call(this.total, el)) {
				sum += parseFloat(this.total[el]);
			}
		}
		// Suplemento de profesores. N-235: a los de pago se SUMAN las plazas de los
		// extras que viajan con el grupo (el coordinador), antes de la puerta —con 0
		// profesores de pago la plaza también se cobra—, como hace el motor.
		const cobrados = PriceHelper.paidTeachers(
			this.budget.students, this.budget.teachers, this.budget.free_teacher_by_students
		) + this.teacherSeats;
		sum += PriceHelper.teacherSupplement(sum, this.budget.supplement, this.budget.students, cobrados);
		return FormatHelper.formatPrice(sum);
	}
}
